import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowLeftRight, Loader2 } from 'lucide-react';
import { getMyFamily, switchProfile } from '../../redux/familySlice';

// Pages that CHANGE something, seen while acting as a read-only profile.
//
// Blurred and covered rather than hidden or redirected, deliberately.
//
// Hiding the page would leave someone hunting for a menu item that used to be
// there and wondering whether the app is broken. Redirecting is worse: it moves
// you somewhere you did not ask to go without saying why. Showing it behind a
// blur says three things at once — the page exists, you are not allowed to use
// it right now, and here is exactly why.
//
// THE BLUR IS NOT THE CONTROL. api/middleware/requireWriteAccess refuses every
// write from a read-only profile whatever this renders; the page underneath
// could not save anything even if the overlay were removed from the DOM. This
// exists so nobody has to discover that by filling in a form first.
export default function ReadOnlyGuard({ children }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { primary, members, activePatientId, loading } = useSelector((s) => s.family || {});

    // Only patients can be acting as somebody else.
    const isPatient = (localStorage.getItem('Role') || '') === 'patient';

    useEffect(() => {
        if (isPatient && !primary) dispatch(getMyFamily());
    }, [isPatient, primary, dispatch]);

    const body = children ?? <Outlet />;
    if (!isPatient) return body;

    const people = [primary, ...(members || [])].filter(Boolean);
    const active = people.find((p) => p.patientId === activePatientId);

    // Not loaded yet, acting as yourself, or holding write — nothing to say.
    // Defaulting to "allowed" while the family is still loading is correct here:
    // this is a courtesy layer, and the server refuses the write regardless.
    if (loading || !active || active.permission !== 'read') return body;

    const name = [active.name, active.surname].filter(Boolean).join(' ').trim();

    const backToOwn = async () => {
        if (!primary?.patientId) return;
        try {
            await dispatch(switchProfile(primary.patientId)).unwrap();
            window.location.reload();
        } catch { /* the reload re-reads the session either way */ }
    };

    return (
        <div className="relative" data-testid="read-only-guard">
            {/* The real page, still there and still readable enough to recognise
                — which is the point. `pointer-events-none` and aria-hidden keep
                it out of reach of the mouse and of a screen reader, so it cannot
                be tabbed into behind the cover. */}
            <div className="pointer-events-none select-none blur-[6px] opacity-60" aria-hidden="true">
                {body}
            </div>

            <div className="absolute inset-0 z-20 flex items-start justify-center bg-white/40 px-4 pt-24 backdrop-blur-[2px]">
                <div className="w-full max-w-md rounded-2xl border-2 border-red-200 bg-white p-6 text-center shadow-xl">
                    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                        <Lock className="h-6 w-6 text-red-500" />
                    </span>

                    <h2 className="mt-3 text-base font-bold text-red-700">
                        {t('family.blocked_title', 'This page is not available')}
                    </h2>

                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        {t('family.blocked_body_a', 'You are viewing')}{' '}
                        <b className="text-gray-800">{name}</b>{' '}
                        {t('family.blocked_body_b',
                           'with read-only access. You can see their records, but you cannot book, cancel or change anything.')}
                    </p>

                    <p className="mt-2 text-xs text-gray-400">
                        {t('family.blocked_hint',
                           'Ask them for Read & write access, or switch back to your own account.')}
                    </p>

                    <button
                        type="button"
                        data-testid="blocked-back-to-own"
                        onClick={backToOwn}
                        className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-[#4ABDBD] py-2.5 text-sm font-semibold text-white transition hover:bg-[#3AA8A8]"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />}
                        {t('family.back_to_my_account', 'Back to my account')}
                    </button>
                </div>
            </div>
        </div>
    );
}
